<template>
    <v-dialog v-model="dialog" persistent max-width="500px">
        <template v-slot:activator="{ on }">
            <v-btn v-if="smallIcon" icon text v-on="on">
                <v-icon>fa-edit</v-icon>
            </v-btn>

            <v-btn v-else color="info" dark v-on="on" class="mb-2" :disabled="!isEditable">
                <v-icon class="pr-1">fa-plus-circle</v-icon>
                Add Qualification
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                <span class="text-h5" v-if="value">Edit Qualification</span>
                <span class="text-h5" v-else>Add Qualification</span>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-form v-model="isFormValid" ref="form" :disabled="!isEditable">
                      <v-row>
                          <v-col cols="12">
                            <v-select label="Qualification Type" outlined dense :items="selectableQualTypes" v-model="selectedQualType"
                            :rules="requiredRule" :disabled="(workingQualifier && workingQualifier.id!==null) || !isEditable" ref="qualTypeSelector"/>
                          </v-col>
                      </v-row>

                      <ach-qualifier-editor v-if="selectedQualType === AchQualifier.CLASS" v-model="workingQualifier"/>
                      <minimum-balance-qualifier-editor v-if="selectedQualType === MinimumBalanceQualifier.CLASS" v-model="workingQualifier"/>
                      <pos-qualifier-editor v-if="selectedQualType === PosQualifier.CLASS" v-model="workingQualifier"/>
                      <credit-or-debit-card-qualifier-editor v-if="selectedQualType === CreditOrDebitCardQualifier.CLASS" v-model="workingQualifier"/>
                      <credit-card-qualifier-editor v-if="selectedQualType === CreditCardQualifier.CLASS" v-model="workingQualifier"/>
                      <loan-qualifier-editor v-if="selectedQualType === LoanQualifier.CLASS" :productTypes="productTypes"
                        :productMappings="productMappings" :accountProductTypes="accountProductTypes" v-model="workingQualifier"/>

                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn large @click="cancel">Cancel</v-btn>
                <v-btn id="saveButton" large class="blue white--text" @click="save" :disabled="!isFormValid || !workingQualifier || !isEditable" ref="okButton">
                        Ok
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  IQualifier, IbLoginQualifier, EStatementQualifier, AchQualifier,
  PosQualifier, CreditCardQualifier, CreditOrDebitCardQualifier, MinimumBalanceQualifier, LinkedAccountQualifier,
  LoanQualifier, VafaOptOutQualifier,
} from '@/api/product/levelrewards/qualifiers';
import { ProductTypeMap } from '@/api/producttypes';
import { AccountProductTypeMap } from '@/api/accountproducttypes';
import { ProductMapping } from '@/api/productmapping';
import { ValidationTool } from '@/util';
import AchQualifierEditor from './AchQualifierEditor.vue';
import MinimumBalanceQualifierEditor from './MinimumBalanceQualifierEditor.vue';
import PosQualifierEditor from './PosQualifierEditor.vue';
import CreditOrDebitCardQualifierEditor from './CreditOrDebitCardQualifierEditor.vue';
import CreditCardQualifierEditor from './CreditCardQualifierEditor.vue';
import LoanQualifierEditor from './LoanQualifierEditor.vue';

@Component({
  components: {
    AchQualifierEditor,
    MinimumBalanceQualifierEditor,
    PosQualifierEditor,
    CreditOrDebitCardQualifierEditor,
    CreditCardQualifierEditor,
    LoanQualifierEditor,
  },
})
export default class EditQualificationDialog extends Vue {
  @Prop()
  value!:IQualifier;

  @Prop({ default: false })
  smallIcon!:boolean;

  @Prop()
  productType!:string;

  @Prop({ default: false })
  isEditable!: boolean;

  @Prop()
  productTypes!: ProductTypeMap;

  @Prop()
  productMappings!: ProductMapping[];

  @Prop()
  accountProductTypes!: AccountProductTypeMap;

  workingQualifier:IQualifier | null = null;

  dialog:boolean = false;

  isFormValid:boolean = true;

  selectedQualType: string | null = null;

  selectableQualTypes: any[] = [];

  // Make visible to the template
  AchQualifier: any = AchQualifier;

  MinimumBalanceQualifier: any = MinimumBalanceQualifier;

  PosQualifier: any = PosQualifier;

  CreditOrDebitCardQualifier: any = CreditOrDebitCardQualifier;

  CreditCardQualifier: any = CreditCardQualifier;

  LoanQualifier: any = LoanQualifier;

  requiredRule:Array<any> = [(value: any) => {
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  requiredAndPositiveRule:Array<any> = [ValidationTool.requiredAndPositiveRuleFunction];

  mounted() {
    this.initialize();
  }

  /* eslint-disable no-underscore-dangle */
  @Watch('dialog')
  initialize() {
    if (this.dialog) {
      if (this.value) {
        this.workingQualifier = this.value.clone();
        this.selectedQualType = this.value._class;
      } else {
        this.workingQualifier = null;
        this.selectedQualType = null;
      }
      this.buildSelectableQualTypes();
    }
  }

  buildSelectableQualTypes() {
    this.selectableQualTypes = [
      {
        text: AchQualifier.NAME,
        value: AchQualifier.CLASS,
      },
      {
        text: EStatementQualifier.NAME,
        value: EStatementQualifier.CLASS,
      },
      {
        text: MinimumBalanceQualifier.NAME,
        value: MinimumBalanceQualifier.CLASS,
      },
      {
        text: IbLoginQualifier.NAME,
        value: IbLoginQualifier.CLASS,
      },
      {
        text: LoanQualifier.NAME,
        value: LoanQualifier.CLASS,
      }];

    if (!this.productType.endsWith('_EDGE')) {
      this.selectableQualTypes.push({
        text: PosQualifier.NAME,
        value: PosQualifier.CLASS,
      });
    }

    this.selectableQualTypes.push({
      text: CreditCardQualifier.NAME,
      value: CreditCardQualifier.CLASS,
    });

    if (!this.productType.endsWith('_EDGE')) {
      this.selectableQualTypes.push({
        text: CreditOrDebitCardQualifier.NAME,
        value: CreditOrDebitCardQualifier.CLASS,
      });
    }

    this.selectableQualTypes.push({
      text: VafaOptOutQualifier.NAME,
      value: VafaOptOutQualifier.CLASS,
    });
  }

  @Watch('selectedQualType')
  onNewSelectedQualType() {
    if (this.selectedQualType && (this.workingQualifier === null || this.workingQualifier._class !== this.selectedQualType)) {
      switch (this.selectedQualType) {
        case AchQualifier.CLASS: {
          this.workingQualifier = new AchQualifier();
          break;
        }

        case CreditCardQualifier.CLASS: {
          this.workingQualifier = new CreditCardQualifier();
          break;
        }

        case CreditOrDebitCardQualifier.CLASS: {
          this.workingQualifier = new CreditOrDebitCardQualifier();
          break;
        }

        case EStatementQualifier.CLASS: {
          this.workingQualifier = new EStatementQualifier();
          break;
        }

        case IbLoginQualifier.CLASS: {
          this.workingQualifier = new IbLoginQualifier();
          break;
        }

        case LinkedAccountQualifier.CLASS: {
          this.workingQualifier = new LinkedAccountQualifier();
          break;
        }

        case MinimumBalanceQualifier.CLASS: {
          this.workingQualifier = new MinimumBalanceQualifier();
          break;
        }

        case PosQualifier.CLASS: {
          this.workingQualifier = new PosQualifier();
          break;
        }

        case LoanQualifier.CLASS: {
          this.workingQualifier = new LoanQualifier();
          break;
        }

        case VafaOptOutQualifier.CLASS: {
          this.workingQualifier = new VafaOptOutQualifier();
          break;
        }

        default:
          throw new Error(`Unknown qualifier type: ${this.selectedQualType}`);
      }
    }
  }

  cancel() {
    const form: any = this.$refs.form;
    form.reset();
    this.dialog = false;
  }

  save() {
    this.$emit('input', this.workingQualifier);
    this.dialog = false;
  }

  /* eslint-enable no-underscore-dangle */
}
</script>

<style lang="scss" scoped>
</style>
